'use client';

import { RefObject, useCallback, useEffect, useState } from 'react';
import { observeElementSize } from 'src/components/primitives/Table/observeElementSize';

type ScrollDirection = 'horizontal' | 'vertical';
type ScrollPosition = 'start' | 'end';

interface UseScrollPositionOptions {
	direction: ScrollDirection;
	position: ScrollPosition;
}

export function useScrollPosition(
	containerRef: RefObject<HTMLElement | null>,
	{ direction, position }: UseScrollPositionOptions
): boolean {
	const [isAtPosition, setIsAtPosition] = useState(false);

	const updateScrollState = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;

		let atPosition: boolean;

		if (direction === 'horizontal') {
			const { scrollLeft, scrollWidth, clientWidth } = container;
			atPosition =
				position === 'start'
					? scrollLeft <= 1
					: scrollLeft + clientWidth >= scrollWidth - 1;
		} else {
			const { scrollTop, scrollHeight, clientHeight } = container;
			atPosition =
				position === 'start'
					? scrollTop <= 1
					: scrollTop + clientHeight >= scrollHeight - 1;
		}

		setIsAtPosition((prev) => (prev === atPosition ? prev : atPosition));
	}, [containerRef, direction, position]);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		updateScrollState();

		container.addEventListener('scroll', updateScrollState);
		const unobserve = observeElementSize(container, updateScrollState);

		return () => {
			container.removeEventListener('scroll', updateScrollState);
			unobserve();
		};
	}, [containerRef, updateScrollState]);

	return isAtPosition;
}
